
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { TenderDetails } from "./TenderDetails";
import { TenderCard } from "./TenderCard";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { debugTranslation } from "@/utils/translationDebugger";

interface TenderWorkflowProps {
  tenders: any[];
  onStatusUpdate: () => void;
}

// Maximum file size for uploads (10MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024; 

// Valid status values that match the database constraint
const VALID_STATUSES = ['not_started', 'in_process', 'ready_to_submit', 'submitted', 'won', 'lost', 'saved'];

export const TenderWorkflow = ({ tenders, onStatusUpdate }: TenderWorkflowProps) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [selectedTender, setSelectedTender] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [localTenders, setLocalTenders] = useState<any[]>(tenders);
  const queryClient = useQueryClient();

  // Update local tenders when props change
  useEffect(() => {
    setLocalTenders(tenders);
  }, [tenders]);

  const updateTenderStatus = async (tenderId: string, status: string) => {
    try {
      console.log(`Attempting to update status to: ${status}`);
      
      // Validate that the status is allowed by the database constraint
      if (!VALID_STATUSES.includes(status)) {
        console.error(`Invalid status: ${status}. Must be one of ${VALID_STATUSES.join(', ')}`);
        throw new Error(`Invalid status: ${status}. Must be one of ${VALID_STATUSES.join(', ')}`);
      }
      
      const { error } = await supabase
        .from('saved_tenders')
        .update({ status })
        .eq('id', tenderId);

      if (error) {
        console.error('Error updating tender status:', error);
        throw error;
      }

      // Update local state
      setLocalTenders(prev => 
        prev.map(tender => 
          tender.id === tenderId ? { ...tender, status } : tender
        )
      );

      toast({
        title: t("dashboard.workflow.statusUpdated", "Status opdateret"),
        description: t("dashboard.workflow.statusUpdateSuccess", "Udbudsstatus blev opdateret"),
      });

      onStatusUpdate();
    } catch (error) {
      console.error('Error updating tender status:', error);
      toast({
        title: t("common.error", "Fejl"),
        description: t("dashboard.workflow.statusUpdateError", "Kunne ikke opdatere status"),
        variant: "destructive",
      });
    }
  };

  const handleTenderClick = (tender: any) => {
    setSelectedTender(tender);
    setIsDetailsOpen(true);
  };

  const handleTenderDelete = async (tenderId: string) => {
    try {
      console.log('Starting deletion process for tender:', tenderId);

      // Update local state immediately for optimistic UI
      setLocalTenders(prevTenders => prevTenders.filter(tender => tender.id !== tenderId));

      // Handle storage files from tender documents
      const { data: tenderDocs, error: tenderDocsError } = await supabase
        .from('tender_documents')
        .select('id, file_path')
        .eq('tender_id', tenderId);

      if (tenderDocsError) {
        console.error('Error fetching tender documents:', tenderDocsError);
        // Continue with deletion even if we can't fetch docs
      } else if (tenderDocs && tenderDocs.length > 0) {
        // Delete storage files for tender documents
        for (const doc of tenderDocs) {
          if (doc.file_path) {
            const { error: storageError } = await supabase.storage
              .from('tender_documents')
              .remove([doc.file_path]);
              
            if (storageError) {
              console.warn('Error deleting document file from storage:', storageError);
              // Continue even if storage deletion fails
            }
          }
        }
      }

      // Thanks to our ON DELETE CASCADE constraints, we can now safely delete the tender
      // and all related records (documents, translations, legal assistance requests, etc.)
      // will be automatically deleted
      const { error: tenderError } = await supabase
        .from('saved_tenders')
        .delete()
        .eq('id', tenderId);

      if (tenderError) {
        throw tenderError;
      }

      // Close the details modal if it's open
      if (selectedTender?.id === tenderId) {
        setIsDetailsOpen(false);
        setSelectedTender(null);
      }

      // Show success message
      toast({
        title: t("common.success", "Succes"),
        description: t("dashboard.workflow.deleteSuccess", "Udbuddet blev slettet"),
      });

      // Trigger parent update and invalidate queries
      onStatusUpdate();
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['owned-tenders'] }),
        queryClient.invalidateQueries({ queryKey: ['team-tenders'] })
      ]);

    } catch (error) {
      console.error('Error in deletion process:', error);
      
      // Revert local state on error
      setLocalTenders(tenders);
      
      toast({
        title: t("common.error", "Fejl"),
        description: t("dashboard.workflow.deleteError", "Kunne ikke slette udbuddet"),
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">{t("dashboard.workflow.title", "Udbudsforløb")}</h2>
      <div className="space-y-4">
        {localTenders.map((tender) => (
          <TenderCard
            key={tender.id}
            tender={tender.tender_data || tender}
            onStatusChange={(status) => updateTenderStatus(tender.id, status)}
            onClick={() => handleTenderClick(tender)}
            onDelete={() => handleTenderDelete(tender.id)}
            viewMode="saved"
          />
        ))}
        {localTenders.length === 0 && (
          <div className="text-center py-8 text-gray-600">
            {t("dashboard.workflow.noSavedTenders", "Ingen gemte udbud")}
          </div>
        )}
      </div>
      <TenderDetails
        tender={selectedTender?.tender_data || selectedTender}
        isOpen={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
      />
    </div>
  );
};
